
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { formatDistanceToNow } from 'date-fns';

interface UserActivityFeedProps {
  userId: string;
  userName?: string;
}

const UserActivityFeed = ({ userId, userName }: UserActivityFeedProps) => {
  const { t } = useLanguage();

  const { data: activities, isLoading } = useQuery({
    queryKey: ['user-activity-feed', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select(`
          *,
          profiles!actor_id(full_name)
        `)
        .eq('actor_id', userId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching user activities:', error);
        return [];
      }

      return data || [];
    }
  });

  const getActionColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create': return 'bg-green-100 text-green-800';
      case 'update': return 'bg-blue-100 text-blue-800';
      case 'delete': return 'bg-red-100 text-red-800';
      case 'verify': return 'bg-purple-100 text-purple-800';
      case 'kyc_status_update': return 'bg-orange-100 text-orange-800';
      case 'role_update': return 'bg-indigo-100 text-indigo-800';
      case 'profile_update': return 'bg-teal-100 text-teal-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {t('user_activity', 'نشاط المستخدم', 'User Activity')}
          </CardTitle>
          <CardDescription>
            {userName && `${t('activity_for', 'النشاط لـ', 'Activity for')} ${userName}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-2 h-2 bg-muted rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('user_activity', 'نشاط المستخدم', 'User Activity')}
        </CardTitle>
        <CardDescription>
          {userName && `${t('activity_for', 'النشاط لـ', 'Activity for')} ${userName}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {activities?.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-4">
              <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge className={getActionColor(activity.action)}>
                    {activity.action}
                  </Badge>
                  <span className="text-sm font-medium">
                    {activity.profiles?.full_name || t('system', 'النظام', 'System')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {activity.entity} {activity.entity_id && `• ${activity.entity_id.slice(0, 8)}...`}
                </p>
                {activity.meta && Object.keys(activity.meta).length > 0 && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {Object.entries(activity.meta).map(([key, value]) => (
                      <span key={key} className="inline-block mr-2">
                        {key}: {String(value).slice(0, 20)}
                        {String(value).length > 20 && '...'}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          ))}
          {(!activities || activities.length === 0) && (
            <p className="text-center text-muted-foreground py-8">
              {t('no_activities', 'لا توجد أنشطة', 'No recent activities for this user')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserActivityFeed;
